import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Agent {
  Id: number;
  Name: string;
  Description: string;
  Status: string;
  CurrentQuestionnaireId: number | null;
  PublishedBuildId: number | null;
  CreatedAt: string;
  ModifiedAt: string | null;
}

export interface AgentsApiResponse {
  agents: Agent[];
  count: number;
}

export interface StatusMapping {
  [studyName: string]: string;
}

export interface ResponseBranch {
  condition: string;
  statusMappings: StatusMapping;
  actions: any | null;
}

export interface ScriptQuestion {
  numericId: number;
  questionID: string;
  text: string;
  category: string | null;
  note: string | null;
  responseBranches: ResponseBranch[];
}

export interface QuestionnaireData {
  script: ScriptQuestion[];
}

export interface CurrentQuestionnaire {
  Id: number;
  AgentId: number;
  Version: number;
  QuestionnaireData: QuestionnaireData;
  CreatedBy: string;
  CreatedAt: string;
  ModifiedBy: string;
  ModifiedAt: string;
}

export interface AgentStudy {
  Id: number;
  Name: string;
  AgentId: number;
  StudyId: number;
  Priority: string | null;
  Enabled: boolean;
  CreatedBy: string;
  CreatedAt: string;
  ModifiedBy: string | null;
  ModifiedAt: string | null;
  Description?: string;
}

export interface PublishedBuild {
  Id: number;
  AgentId: number;
  QuestionnaireId: number;
  BuildVersion: number;
  QuestionnaireVersion: number;
  Status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  BuildStartedAt: string;
  BuildCompletedAt: string | null;
  BuildLog: string | null;
  CreatedBy: string;
  CreatedAt: string;
  ModifiedBy: string;
  ModifiedAt: string;
}

export interface AgentDetails {
  Id: number;
  Name: string;
  Description: string;
  Status: string;
  CurrentQuestionnaireId: number | null;
  PublishedBuildId: number | null;
  PublishedBuild: PublishedBuild | null;
  CreatedAt: string;
  ModifiedAt: string | null;
  CurrentQuestionnaire: CurrentQuestionnaire | null;
  AgentStudies: AgentStudy[];
}

export interface UnassignedStudy {
  Id: number;
  Name: string;
  AgentId: number;
  StudyId: number;
  Priority: string;
  Enabled: boolean;
  CreatedBy: string;
  CreatedAt: string;
  ModifiedBy: string;
  ModifiedAt: string;
  Description: string;
}

export interface UnassignedStudiesResponse {
  studies: UnassignedStudy[];
  count: number;
}

export interface UpdateStudyRequest {
  Enabled: boolean;
  AgentId: number;
  Priority: string;
  Description: string;
  ModifiedBy: string;
}

export interface DuplicateAgentResponse {
  success: boolean;
  message: string;
  agent: Agent;
}

export interface CreateAgentRequest {
  Name: string;
  Description: string;
  AgentType: string;
  CreatedBy: string;
}

export interface CreateAgentResponse {
  success: boolean;
  message: string;
  agent: Agent;
}

export interface AuditTrailEntry {
  Id: number;
  AgentId: number | null;
  AgentName: string | null;
  Action: string;
  EntityType: string;
  EntityId: number | null;
  PreviousState: Record<string, any> | null;
  CurrentState: Record<string, any> | null;
  Details: Record<string, any> | null;
  UpdatedBy: string;
  UpdatedAt: string;
}

export interface AuditTrailResponse {
  records: AuditTrailEntry[];
  count: number;        // records in this page
  total_count: number;  // total matching rows (for pagination)
}

@Injectable({
  providedIn: 'root'
})
export class AgentService {

  private readonly baseUrl = `${environment.comforceURL}/agents`;
  private readonly studiesBaseUrl = `${environment.comforceURL}/studies`;
  private readonly comforceApiBaseUrl = `${environment.apiBaseUrl}/agent`;

  constructor(private http: HttpClient) { }

  getAgents(): Observable<AgentsApiResponse> {
    const headers = new HttpHeaders({
      'accept': 'application/json'
    });

    return this.http.get<AgentsApiResponse>(this.baseUrl, { headers });
  }

  getAgentById(id: number): Observable<AgentDetails> {
    const headers = new HttpHeaders({
      'accept': 'application/json'
    });

    return this.http.get<AgentDetails>(`${this.baseUrl}/${id}`, { headers });
  }

  getUnassignedStudies(): Observable<UnassignedStudiesResponse> {
    const headers = new HttpHeaders({
      'accept': 'application/json'
    });

    return this.http.get<UnassignedStudiesResponse>(`${this.studiesBaseUrl}/unassigned`, { headers });
  }

  updateStudy(studyId: number, payload: UpdateStudyRequest): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'accept': 'application/json'
    });

    return this.http.patch<any>(`${this.studiesBaseUrl}/${studyId}`, payload, { headers });
  }

  duplicateAgent(agentId: number): Observable<DuplicateAgentResponse> {
    const headers = new HttpHeaders({
      'accept': 'application/json'
    });

    return this.http.put<DuplicateAgentResponse>(`${this.baseUrl}/${agentId}`, {}, { headers });
  }

  createAgent(payload: CreateAgentRequest): Observable<CreateAgentResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'accept': 'application/json'
    });

    return this.http.post<CreateAgentResponse>(`${this.baseUrl}/create`, payload, { headers });
  }

  checkPendingCalls(agentId: number): Observable<{ hasPendingCalls: boolean; pendingCallCount: number; isAssignedToPublishedAIPool: boolean; assignedPoolNames: string[] }> {
    const headers = new HttpHeaders({ 'accept': 'application/json', 'API_KEY': environment.apiKey });
    return this.http.get<{ hasPendingCalls: boolean; pendingCallCount: number; isAssignedToPublishedAIPool: boolean; assignedPoolNames: string[] }>(
      `${this.comforceApiBaseUrl}/${agentId}/has-pending-calls`, { headers }
    );
  }

  deleteAgent(agentId: number): Observable<any> {
    const headers = new HttpHeaders({ 'accept': 'application/json', 'API_KEY': environment.apiKey });
    return this.http.delete<any>(`${this.comforceApiBaseUrl}/${agentId}`, { headers });
  }

  getAuditTrail(
    fromDate?: string,
    toDate?: string,
    limit: number = 10,
    offset: number = 0
  ): Observable<AuditTrailResponse> {
    const headers = new HttpHeaders({ 'accept': 'application/json' });
    let params = `?limit=${limit}&offset=${offset}`;
    if (fromDate) params += `&from_date=${encodeURIComponent(fromDate)}`;
    if (toDate) params += `&to_date=${encodeURIComponent(toDate)}`;
    return this.http.get<AuditTrailResponse>(`${this.baseUrl}/audit-trail${params}`, { headers });
  }
}

